/**
 * Phase 1 demo seed for ATW Workforce UI. Replace with real org APIs later.
 * Agents are unlimited. Usage is the shared credit pool.
 * No em dashes in customer-facing copy fields.
 */

import type { AgentMailProvider, PersonalMailStatus } from "@/lib/agentPersonalMail";
import { migrateAgentPersonalMailFields } from "@/lib/agentPersonalMail";

export type { AgentMailProvider, PersonalMailStatus } from "@/lib/agentPersonalMail";
export {
  AGENT_MAIL_PROVIDER_OPTIONS,
  agentMailProviderLabel,
} from "@/lib/agentPersonalMail";

export type AgentsPlanId = "standard" | "plus" | "premier";

export type AgentTypeId =
  | "lead"
  | "enrich"
  | "sales"
  | "support"
  | "operations"
  | "accounting"
  | "customer_success"
  | "marketing"
  | "reception"
  | "scheduler"
  | "fulfillment"
  | "librarian";

export type AgentStatus = "active" | "paused" | "archived";

/** How the agent's mailbox address was chosen. Missing on older saved agents. */
export type MailboxSource = "existing" | "created";

/**
 * Legacy exclusive mode. Agents may now have a personal mailbox and linked
 * shared inboxes at the same time. Prefer mailboxAddress + sharedInboxIds.
 */
export type AgentEmailMode = "personal" | "shared";

/** What the agent may do on one email binding (personal address or shared inbox). */
export type AgentEmailCapabilities = {
  create: boolean;
  draft: boolean;
  edit: boolean;
  delete: boolean;
};

/**
 * Pre-connect / disconnected baseline (and older connect defaults).
 * After a successful personal-mail OAuth, prefer
 * `possibleEmailCapabilitiesForProvider` instead of this.
 */
export const DEFAULT_AGENT_EMAIL_CAPABILITIES: AgentEmailCapabilities = {
  create: true,
  draft: true,
  edit: false,
  delete: false,
};

/** Max caps when the connection/scopes allow create, draft, edit, and delete. */
export const FULL_AGENT_EMAIL_CAPABILITIES: AgentEmailCapabilities = {
  create: true,
  draft: true,
  edit: true,
  delete: true,
};

export type TimelineResult = "success" | "fail" | "partial" | "pending" | "stopped";

export type TimelineKind =
  | "wake"
  | "playbook_step"
  | "tool_call"
  | "skill_call"
  | "result"
  | "credit_burn"
  | "stop"
  | "escalation"
  | "human_resolution"
  | "config_change"
  | "pause"
  | "resume";

export type ConnectionStatus = "connected" | "not_connected" | "error" | "pending";

export type ConnectionKind = "atw" | "oauth" | "api" | "mcp";

export type AgentsHumanRoleId = "super_admin" | "standard" | string;

export type OrgAgent = {
  id: string;
  name: string;
  /** One sentence for what this agent is hired to do. Optional on older saved agents. */
  jobSummary?: string;
  /** Older saved agents may still have a type. New agents omit it. The UI ignores it. */
  agentType?: AgentTypeId;
  agentTypeLabel?: string;
  status: AgentStatus;
  /** Org user id of the owner. Older saved agents may not have one. */
  humanOwnerUserId?: string;
  humanOwnerName: string;
  humanOwnerEmail: string;
  /** Other people in the organization who can open this agent. The owner is included automatically. */
  sharedWithUserIds?: string[];
  /**
   * This agent's own mailbox (personal / agent login).
   * Null until connected on Settings Email.
   * Omitted on agents saved before this field existed (treat that as null).
   */
  mailboxAddress?: string | null;
  /** Whether the address was an existing mailbox or one entered for this agent. */
  mailboxSource?: MailboxSource | null;
  /**
   * Mail server this agent signed into for its personal mailbox.
   * Agent-owned. Not an org Connections row.
   */
  personalMailProvider?: AgentMailProvider | null;
  /**
   * Honest agent-owned connect state. Prefer this over personalMailConnected.
   * disconnected | pending | connected. Connected requires a mailbox.
   */
  personalMailStatus?: PersonalMailStatus;
  /**
   * Legacy boolean from the Phase 1 magic-click stub.
   * Kept for older snapshots; migrate via agentPersonalMail helpers.
   * Do not set true unless personalMailStatus is connected with a mailbox.
   */
  personalMailConnected?: boolean;
  /**
   * Legacy exclusive personal vs shared flag. Newer UI allows both at once.
   * Prefer mailboxAddress and sharedInboxIds.
   */
  emailMode?: AgentEmailMode | null;
  /** Org shared inbox ids this agent may also use (team shared inboxes). */
  sharedInboxIds?: string[];
  /** Capabilities for the personal / agent mailbox binding. */
  personalEmailCapabilities?: AgentEmailCapabilities;
  /** Capabilities keyed by shared inbox id. */
  sharedInboxCapabilities?: Record<string, AgentEmailCapabilities>;
  /**
   * Organization schedule ids assigned to this agent.
   * Omitted on agents saved before schedules existed (treat that as none).
   */
  scheduleIds?: string[];
  lastActiveAt: string;
  creditsBurnedMonth: number;
};

/** Team shared inbox. Org asset agents attach to. Not a person's personal email. */
export type OrgSharedInbox = {
  id: string;
  name: string;
  emailAddress: string;
  /** Org connection id that is mail-capable (ATW Mail / Google / Microsoft style). */
  connectionId: string;
  /** Optional note. Empty or omitted when none was entered. */
  note?: string;
};

export type TimelineEvent = {
  id: string;
  agentId: string;
  runId: string;
  at: string;
  kind: TimelineKind;
  summary: string;
  result?: TimelineResult;
  credits?: number;
  actor: "agent" | "human" | "system";
  humanUserId?: string;
  externalRefs?: { label: string; id: string }[];
};

export type OverseerRow = {
  id: string;
  agentId: string;
  agentName: string;
  status: "done" | "stalled" | "exception";
  summary: string;
  timelineEventId: string;
  updatedAt: string;
};

/** How often a saved organization schedule is meant to run. Not a live runner. */
export type ScheduleCadence = "business_hours" | "every_few_minutes" | "morning_pass";

export type OrgSchedule = {
  id: string;
  name: string;
  cadence: ScheduleCadence;
  /** Optional note. Empty or omitted when none was entered. */
  note?: string;
};

export type OrgConnection = {
  id: string;
  name: string;
  /** How this connection will connect. Not a live credential. */
  kind: ConnectionKind;
  provider: string;
  status: ConnectionStatus;
  note: string;
  /**
   * Set on create when someone marks "This is a mail connection".
   * Older saved connections omit it. Name and provider can still count as mail.
   */
  mail?: boolean;
};

export type AgentsOrgUser = {
  id: string;
  name: string;
  email: string;
  roles: AgentsHumanRoleId[];
};

export type AgentsRole = {
  id: AgentsHumanRoleId;
  name: string;
  builtIn: boolean;
  description: string;
  permissions: string[];
  viewOnlyDefault?: boolean;
};

export type AgentsDemoOrg = {
  orgId: string;
  name: string;
  plan: AgentsPlanId;
  planLabel: string;
  maxAddon: boolean;
  creditsIncluded: number;
  creditsUsed: number;
  agents: OrgAgent[];
  timeline: TimelineEvent[];
  overseer: OverseerRow[];
  connections: OrgConnection[];
  schedules: OrgSchedule[];
  sharedInboxes: OrgSharedInbox[];
  users: AgentsOrgUser[];
  roles: AgentsRole[];
};

export const AGENT_TYPE_OPTIONS: { id: AgentTypeId; label: string }[] = [
  { id: "lead", label: "Lead" },
  { id: "enrich", label: "Enrich" },
  { id: "sales", label: "Sales" },
  { id: "support", label: "Support" },
  { id: "operations", label: "Operations" },
  { id: "accounting", label: "Accounting" },
  { id: "customer_success", label: "Customer success" },
  { id: "marketing", label: "Marketing" },
  { id: "reception", label: "Reception" },
  { id: "scheduler", label: "Scheduler" },
  { id: "fulfillment", label: "Fulfillment" },
  { id: "librarian", label: "Librarian" },
];

export function agentTypeLabel(type: AgentTypeId): string {
  return AGENT_TYPE_OPTIONS.find((o) => o.id === type)?.label ?? type;
}

export type AgentDirectoryPerson = {
  id: string;
  name: string;
  email: string;
};

export type AgentDirectorySession = {
  id?: string;
  name?: string | null;
  email?: string | null;
} | null;

function sameDirectoryPerson(
  person: AgentDirectoryPerson,
  sessionUser: AgentDirectorySession | undefined,
): boolean {
  const id = sessionUser?.id?.trim();
  const email = sessionUser?.email?.trim().toLowerCase();
  if (id && person.id === id) return true;
  if (email && person.email.toLowerCase() === email) return true;
  return false;
}

/** Org users, plus the signed-in user when that person is not already listed. */
export function agentDirectory(
  users: AgentsOrgUser[],
  sessionUser?: AgentDirectorySession,
): AgentDirectoryPerson[] {
  const people: AgentDirectoryPerson[] = [];
  for (const user of users) {
    const id = user.id.trim();
    const name = user.name.trim();
    const email = user.email.trim();
    if (!id || !name || !email) continue;
    if (
      people.some(
        (person) => person.id === id || person.email.toLowerCase() === email.toLowerCase(),
      )
    ) {
      continue;
    }
    people.push({ id, name, email });
  }

  const email = sessionUser?.email?.trim() || "";
  const name = sessionUser?.name?.trim() || "";
  const id = sessionUser?.id?.trim() || "";
  if (!email || people.some((person) => sameDirectoryPerson(person, sessionUser))) return people;

  const slug = email.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  let sessionId = id || `user_${slug || "session"}`;
  if (people.some((person) => person.id === sessionId)) sessionId = `${sessionId}_session`;
  people.push({
    id: sessionId,
    name: name || email,
    email,
  });
  return people;
}

/** Signed-in user when they have a name, otherwise the first org user. */
export function defaultAgentOwnerId(
  people: AgentDirectoryPerson[],
  sessionUser?: AgentDirectorySession,
): string {
  if (people.length === 0) return "";
  const sessionName = sessionUser?.name?.trim();
  if (!sessionName) return people[0].id;
  const match =
    people.find((person) => sameDirectoryPerson(person, sessionUser)) ??
    people.find((person) => person.name.toLowerCase() === sessionName.toLowerCase());
  return match?.id ?? people[0].id;
}

export const CONNECTION_KIND_OPTIONS: { id: ConnectionKind; label: string }[] = [
  { id: "atw", label: "ATW" },
  { id: "oauth", label: "OAuth" },
  { id: "api", label: "API" },
  { id: "mcp", label: "MCP" },
];

export function connectionKindLabel(kind: ConnectionKind): string {
  return CONNECTION_KIND_OPTIONS.find((option) => option.id === kind)?.label ?? kind;
}

const MAIL_PROVIDER_IDS = new Set(["atw_mail", "google", "microsoft"]);

/** ATW Mail, Gmail, Google, Outlook, Microsoft, or the word mail. */
const MAIL_TEXT_RE = /\b(?:atw[\s_-]*mail|gmail|google|outlook|microsoft|mail)\b/i;

export function isMailConnection(conn: {
  name: string;
  kind: string;
  provider: string;
  mail?: boolean;
}): boolean {
  if (conn.mail === true) return true;
  const providerKey = conn.provider.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (MAIL_PROVIDER_IDS.has(providerKey)) return true;
  return MAIL_TEXT_RE.test(`${conn.name} ${conn.kind} ${conn.provider}`);
}

export function orgHasMailConnection(
  connections: readonly { name: string; kind: string; provider: string; mail?: boolean }[],
): boolean {
  return connections.some((conn) => isMailConnection(conn));
}

const MAILBOX_ADDRESS_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Trimmed address, or null when it is not a single email address. */
export function normalizeMailboxAddress(value: string): string | null {
  const address = value.trim();
  if (!MAILBOX_ADDRESS_RE.test(address)) return null;
  return address;
}

/** Join a local part and domain into one address. Does not call a mail provider. */
export function mailboxFromLocalPart(localPart: string, domain: string): string | null {
  const local = localPart.trim();
  const host = domain.trim().replace(/^@+/, "");
  if (!local || !host || /[\s@]/.test(local) || /[\s@]/.test(host)) return null;
  return normalizeMailboxAddress(`${local}@${host}`);
}

export function agentMailboxAddress(agent: Pick<OrgAgent, "mailboxAddress">): string | null {
  const address = agent.mailboxAddress?.trim() ?? "";
  return address || null;
}

export function newAgentId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return `agent_${slug || "new"}_${Date.now().toString(36)}`;
}

export const SCHEDULE_CADENCE_OPTIONS: { id: ScheduleCadence; label: string }[] = [
  { id: "business_hours", label: "Business hours" },
  { id: "every_few_minutes", label: "Every few minutes" },
  { id: "morning_pass", label: "Morning pass" },
];

export function scheduleCadenceLabel(cadence: ScheduleCadence): string {
  return SCHEDULE_CADENCE_OPTIONS.find((option) => option.id === cadence)?.label ?? cadence;
}

export function newScheduleId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return `sched_${slug || "new"}_${Date.now().toString(36)}`;
}

export function newConnectionId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return `conn_${slug || "new"}_${Date.now().toString(36)}`;
}

export function newSharedInboxId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  return `inbox_${slug || "new"}_${Date.now().toString(36)}`;
}

export function normalizeEmailCapabilities(
  value?: AgentEmailCapabilities | null,
): AgentEmailCapabilities {
  if (!value) return { ...DEFAULT_AGENT_EMAIL_CAPABILITIES };
  return {
    create: Boolean(value.create),
    draft: Boolean(value.draft),
    edit: Boolean(value.edit),
    delete: Boolean(value.delete),
  };
}

export function emailCapabilitiesEqual(
  a: AgentEmailCapabilities,
  b: AgentEmailCapabilities,
): boolean {
  return (
    a.create === b.create &&
    a.draft === b.draft &&
    a.edit === b.edit &&
    a.delete === b.delete
  );
}

/**
 * Capabilities the personal-mail connection can actually support, given the
 * OAuth scopes we request for that provider today.
 *
 * Google: gmail.send + gmail.compose + gmail.modify → create, draft, edit, delete.
 * Microsoft: Mail.Send + Mail.ReadWrite → all four.
 * Zoho: ZohoMail.messages.ALL → all four.
 * ATW Mail: session-bound full mailbox → all four.
 *
 * Narrower future providers should return only what their scopes allow.
 */
export function possibleEmailCapabilitiesForProvider(
  provider: AgentMailProvider | null | undefined,
): AgentEmailCapabilities {
  switch (provider) {
    case "google":
    case "microsoft":
    case "zoho":
    case "atw_mail":
      return { ...FULL_AGENT_EMAIL_CAPABILITIES };
    default:
      return { ...DEFAULT_AGENT_EMAIL_CAPABILITIES };
  }
}

/**
 * Gentle upgrade: agents connected under the old create+draft defaults while
 * the provider token already allows more. Matches old defaults only (not every
 * restricted policy). Persist after load so it runs once.
 */
export function migratePersonalEmailCapabilities<T extends OrgAgent>(agent: T): T {
  const status = agent.personalMailStatus;
  const provider = agent.personalMailProvider;
  if (status !== "connected" || !provider) return agent;

  const current = normalizeEmailCapabilities(agent.personalEmailCapabilities);
  if (!emailCapabilitiesEqual(current, DEFAULT_AGENT_EMAIL_CAPABILITIES)) {
    return agent;
  }

  const possible = possibleEmailCapabilitiesForProvider(provider);
  if (emailCapabilitiesEqual(current, possible)) return agent;

  return { ...agent, personalEmailCapabilities: possible };
}

/** Personal-mail status scrub + capability upgrade used on roster load. */
export function migrateOrgAgent(agent: OrgAgent): OrgAgent {
  return migratePersonalEmailCapabilities(migrateAgentPersonalMailFields(agent));
}

export function migrateOrgAgents(agents: OrgAgent[]): OrgAgent[] {
  return agents.map(migrateOrgAgent);
}

export const AGENTS_DEMO_ORG: AgentsDemoOrg = {
  orgId: "4133643",
  name: "Demo Company",
  plan: "plus",
  planLabel: "Plus",
  maxAddon: false,
  creditsIncluded: 10_000,
  creditsUsed: 0,
  agents: [],
  timeline: [],
  overseer: [],
  connections: [],
  schedules: [],
  sharedInboxes: [],
  users: [
    {
      id: "user_alex",
      name: "Alex Rivera",
      email: "alex@demo.example",
      roles: ["super_admin", "standard"],
    },
    {
      id: "user_jordan",
      name: "Jordan Lee",
      email: "jordan@demo.example",
      roles: ["standard"],
    },
    {
      id: "user_sam",
      name: "Sam Patel",
      email: "sam@demo.example",
      roles: ["standard"],
    },
  ],
  roles: [
    {
      id: "super_admin",
      name: "Super Admin",
      builtIn: true,
      description: "Full control of the Agent Organization: agents, config, users, roles, billing.",
      permissions: [
        "manage_users",
        "manage_roles",
        "manage_connections",
        "manage_schedules",
        "manage_agent_configuration",
        "create_edit_archive_agents",
        "manage_credits_plan",
        "act_on_escalations",
        "view_all",
      ],
    },
    {
      id: "standard",
      name: "Standard",
      builtIn: true,
      viewOnlyDefault: true,
      description:
        "Required for every human with Agents access. Default is view only: agents, timelines, Overseer, and credits.",
      permissions: ["view_all"],
    },
  ],
};

export function getDemoAgent(agentId: string): OrgAgent | undefined {
  return AGENTS_DEMO_ORG.agents.find((agent) => agent.id === agentId);
}

export function timelineForAgent(agentId: string): TimelineEvent[] {
  return AGENTS_DEMO_ORG.timeline
    .filter((event) => event.agentId === agentId)
    .sort((a, b) => (a.at < b.at ? 1 : -1));
}

export function creditsRemaining(org: AgentsDemoOrg = AGENTS_DEMO_ORG): number {
  const included = org.maxAddon ? org.creditsIncluded * 20 : org.creditsIncluded;
  return Math.max(0, included - org.creditsUsed);
}

export function effectiveCreditsIncluded(org: AgentsDemoOrg = AGENTS_DEMO_ORG): number {
  return org.maxAddon ? org.creditsIncluded * 20 : org.creditsIncluded;
}
